import { sortBy, map } from "lodash";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import { CSVLink } from "react-csv";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CreateIcon from "@mui/icons-material/Create";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import { deleteAccount, queryAccounts, unlinkAccount, fetchAccountsAutopayStatus } from "./../slices/accounts";
import AccountDialog from "./AccountDialog";
import errorReporter from "../utils/errorReporting";

function AccountsComponent() {
  const dispatch = useDispatch();
  const { accounts, autopayStatus, autopayLoading, autopayLastFetched, loading, error } = useSelector((state) => state.accounts);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const sortedAccounts = useMemo(
    () => sortBy(accounts, "houseNumber"),
    [accounts],
  );

  const getAutopayStatus = useCallback((accountId) => {
    const status = autopayStatus[accountId];
    if (!status) return { status: 'unknown', icon: null, color: 'textSecondary', tooltip: 'Unknown', linkedUsers: [] };
    
    const baseStatus = {
      linkedUsers: status.linkedUsers || []
    };
    
    switch (status.status) {
      case 'active':
        return { 
          ...baseStatus,
          status: 'active', 
          icon: <AutorenewIcon />, 
          color: 'success', 
          tooltip: 'Autopay Active' 
        };
      case 'inactive':
        return { 
          ...baseStatus,
          status: 'inactive', 
          icon: <MoneyOffIcon />, 
          color: 'warning', 
          tooltip: 'No Autopay' 
        };
      case 'not-linked':
        return { 
          ...baseStatus,
          status: 'not-linked', 
          icon: null, 
          color: 'textSecondary', 
          tooltip: 'Account Not Linked' 
        };
      default:
        return { ...baseStatus, status: 'unknown', icon: null, color: 'textSecondary', tooltip: 'Unknown' };
    }
  }, [autopayStatus]);

  const formatLinkedUsers = useCallback((linkedUsers) => {
    if (!linkedUsers || linkedUsers.length === 0) return null;
    
    return linkedUsers.map(user => {
      const name = `${user.firstName} ${user.lastName}`.trim();
      return name ? `${name} (${user.email})` : user.email;
    }).join(', ');
  }, []);

  const csvData = useMemo(() => {
    return map(sortedAccounts, (account) => {
      const autopay = getAutopayStatus(account.id);
      const linkedUsersText = formatLinkedUsers(autopay.linkedUsers) || '';
      return {
        property_address: `${account.houseNumber} ${account.propertyAddress}`,
        linked: account.linked ? 'Yes' : 'No',
        linked_users: linkedUsersText,
        autopay: autopay.tooltip,
        credit: new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(account.credit / 100),
        due: new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(account.amountDue / 100),
        balance: new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format((account.credit - account.amountDue) / 100),
      };
    });
  }, [sortedAccounts, autopayStatus, getAutopayStatus, formatLinkedUsers]);

  useEffect(() => {
    dispatch(queryAccounts())
      .then(() => {
        // Fetch autopay status after accounts are loaded
        console.log('🏠 Accounts loaded, fetching autopay status...');
        return dispatch(fetchAccountsAutopayStatus());
      })
      .catch((error) => {
        errorReporter.reportUIError(error, 'Accounts', 'initial_load', {
          step: 'loading_accounts_and_autopay'
        });
        console.error('Failed to load accounts or autopay status:', error);
      });
  }, [dispatch]);

  const handleRefreshAutopay = () => {
    console.log('🔄 Manual autopay refresh triggered (force refresh)');
    dispatch(fetchAccountsAutopayStatus(true)) // Pass true to force refresh
      .catch((error) => {
        errorReporter.reportAutopayError(error, {
          action: 'manual_refresh',
          trigger: 'admin_button_click'
        });
        console.error('Manual autopay refresh failed:', error);
      });
  };

  const handleDeleteAccount = (id) => {
    dispatch(deleteAccount(id))
      .catch((error) => {
        errorReporter.reportError(error, {
          component: 'Accounts',
          action: 'delete_account',
          account_id: id,
          severity: 'medium'
        });
        console.error('Failed to delete account:', error);
      });
  };

  const handleEditAccount = (id) => {
    setEditId(id);
    setDialogOpen(true);
  };

  const handleUnlinkAccount = (id) => {
    if (window.confirm('Are you sure you want to unlink this account? The resident will need to re-link their account.')) {
      dispatch(unlinkAccount(id))
        .catch((error) => {
          errorReporter.reportError(error, {
            component: 'Accounts',
            action: 'unlink_account',
            account_id: id,
            severity: 'medium'
          });
          console.error('Failed to unlink account:', error);
        });
    }
  };

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditId(null);
  };

  const formatBalance = (cents) => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const dateTime = () => {
    const now = new Date();
    return now.toLocaleString();
  };

  return (
    <Box
      sx={{
        padding: { xs: 1, sm: 2 },
        width: "100%",
        maxWidth: { xs: '100%', sm: 600, md: 800 },
        mx: "auto",
        position: "relative",
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: 2
      }}>
        <Typography
          variant="h4"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '1.8rem' }, 
            fontWeight: 700,
            color: 'text.primary'
          }}
        >
          Accounts
        </Typography>
        
        <Box sx={{ display: "flex", gap: 1, order: { xs: 3, sm: 0 } }}>
          <Tooltip title={`Refresh Autopay Status${autopayLastFetched ? ` (last: ${Math.round((Date.now() - autopayLastFetched) / 60000)}m ago)` : ''}`}>
            <IconButton
              onClick={handleRefreshAutopay}
              disabled={autopayLoading}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                animation: autopayLoading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
                '&:hover': {
                  bgcolor: 'grey.50'
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Download Account Summary">
            <IconButton sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'grey.50'
              }
            }}>
              <CSVLink 
                data={csvData} 
                filename={`accounts${dateTime()}.csv`}
                style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}
              >
                <DownloadIcon fontSize="small" />
              </CSVLink>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Button
        onClick={handleOpenDialog}
        variant="contained"
        color="primary"
        sx={{
          mb: 3,
          width: { xs: '100%', sm: 'auto' },
          fontSize: "1rem",
          py: 1.5,
          px: 3,
          textTransform: "none",
          borderRadius: 3,
          fontWeight: 600
        }}
      >
        Add New Account
      </Button>
      <Box sx={{ display: 'grid', gap: 2 }}>
        {sortedAccounts.map((account) => {
          const autopay = getAutopayStatus(account.id);
          const linkedUsersText = formatLinkedUsers(autopay.linkedUsers);
          
          return (
            <Card 
              key={account.id}
              elevation={1} 
              sx={{ 
                borderRadius: 3, 
                overflow: "hidden",
                transition: 'all 0.2s ease',
                "&:hover": {
                  elevation: 3,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Header with address and status icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 600,
                        color: 'text.primary'
                      }}
                    >
                      {`${account.houseNumber} ${account.propertyAddress}`}
                    </Typography>
                    
                    {/* Status Icons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {account.linked ? (
                        <Tooltip title="Account Linked">
                          <LinkIcon color="primary" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Account Not Linked">
                          <LinkOffIcon color="disabled" fontSize="small" />
                        </Tooltip>
                      )}
                      
                      {autopay.icon && (
                        <Tooltip title={autopay.tooltip}>
                          <Box sx={{ display: 'flex', color: `${autopay.color}.main` }}>
                            {React.cloneElement(autopay.icon, { fontSize: 'small', color: autopay.color })}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleEditAccount(account.id)}
                      sx={{ padding: 1 }}
                    >
                      <CreateIcon fontSize="small" />
                    </IconButton>
                    {account.linked && (
                      <Tooltip title="Unlink Account">
                        <IconButton
                          aria-label="unlink"
                          onClick={() => handleUnlinkAccount(account.id)}
                          sx={{ padding: 1 }}
                        >
                          <LinkOffIcon fontSize="small" color="warning" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      aria-label="delete"
                      disabled
                      onClick={() => handleDeleteAccount(account.id)}
                      sx={{ padding: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                {/* Linked User Info */}
                {linkedUsersText && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="primary.main"
                      sx={{ 
                        fontSize: '0.9rem', 
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        wordBreak: 'break-word'
                      }}
                    >
                      👤 {linkedUsersText}
                    </Typography>
                  </Box>
                )}
                
                {/* Balance and Status Row */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1
                }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Balance:{" "}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: account.credit - account.amountDue < 0 
                            ? 'error.main' 
                            : 'success.main'
                        }}
                      >
                        {formatBalance(account.credit - account.amountDue)}
                      </Typography>
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="caption" 
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: autopay.color === 'success' 
                          ? 'rgba(46, 125, 50, 0.1)' 
                          : autopay.color === 'warning' 
                            ? 'rgba(237, 108, 2, 0.1)' 
                            : 'grey.100',
                        color: autopay.color === 'success' 
                          ? 'success.main' 
                          : autopay.color === 'warning' 
                            ? 'warning.main' 
                            : 'text.secondary'
                      }}
                    >
                      {account.linked ? autopay.tooltip : 'Not Linked'}
                      {autopayLoading && ' ⚡ Loading...'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      <AccountDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editId={editId}
      />
    </Box>
  );
}

const Accounts = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: "100%",
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        bgcolor: "#f8fafc",
      }}
    >
      <AccountsComponent />
    </Box>
  );
};

export default Accounts;
